import { Text } from '@repo/foundations';
import { useMemo } from 'react';
import type { INotification } from '../../../api/types/models/notification';
import type { EnhancedNotification } from '../types';
import {
	formatTeamAccessRequestTeamName,
	isTeamAccessRequestEvent,
} from '../utils/teamAccessRequestUtils';

interface INotificationTextProps {
	notification: INotification;
	className?: string;
	showResourceTitle?: boolean;
	useTextWithLineClamp?: boolean;
	lineClamp?: number;
}

export function NotificationText({
	notification,
	className,
	showResourceTitle = false,
	useTextWithLineClamp = false,
	lineClamp = 1,
}: INotificationTextProps) {
	const { sender } = notification.title;

	const subjectArray = useMemo(() => {
		// Example: "John Doe assigned you to a question"
		// We want to bold the "John Doe"
		// Example: "You have a new message from John Doe"
		// We want to bold the "John Doe"
		const formattedSubjectArray = [];
		// Find the position of the sender in the subject
		const senderIndex = sender
			? notification.subject.toLowerCase().indexOf(sender.toLowerCase())
			: -1;

		// Return the non-bold subject if no sender is found
		if (!sender || senderIndex === -1) {
			return [{ context: notification.subject, bold: false }];
		}

		// Push everything before the sender to the array with non-bold text
		if (senderIndex !== 0) {
			formattedSubjectArray.push({
				context: notification.subject.slice(0, senderIndex),
				bold: false,
			});
		}

		// Push the sender to the array with bold text
		formattedSubjectArray.push({
			context: sender,
			bold: true,
		});

		// Send the rest of the subject to the array with non-bold text
		if (senderIndex + sender.length < notification.subject.length) {
			formattedSubjectArray.push({
				context: notification.subject.slice(senderIndex + sender.length),
				bold: false,
			});
		}

		return formattedSubjectArray;
	}, [notification.subject, sender]);

	const subjectLine = useMemo(
		() => subjectArray.map(({ context }) => context).join(''),
		[subjectArray]
	);

	// Special handling for AI Agent notifications secondary text
	const getAiAgentSecondaryText = (
		enhancedNotification: EnhancedNotification
	) => {
		if (!enhancedNotification.computedMetadata?.isAiAgent) return null;

		if (!showResourceTitle) {
			// This is for the secondary text (subtitle)
			switch (enhancedNotification.event) {
				case 'ai_task_run_completed':
					return 'Agent completed run';
				case 'ai_task_run_failed':
					return 'Agent failed run';
				case 'ai_task_run_timeout':
					return 'Agent timed out';
				default:
					return 'Agent run';
			}
		}
		return null;
	};

	const enhancedNotification = notification as EnhancedNotification;
	const aiAgentSecondaryText = getAiAgentSecondaryText(enhancedNotification);
	const isTeamAccessRequest = isTeamAccessRequestEvent(notification.event);
	const teamName = formatTeamAccessRequestTeamName(
		(notification.metadata?.team_name as string | undefined) ??
			notification.resource_title ??
			'Team'
	);
	const requesterName =
		(notification.metadata?.requester_name as string | undefined) ??
		notification.sender?.display_name ??
		'A user';
	let displayText = aiAgentSecondaryText;

	if (!displayText && isTeamAccessRequest) {
		displayText = showResourceTitle
			? `Access Request for ${teamName}`
			: `${requesterName} requested access to the ${teamName}`;
	}

	if (!displayText) {
		displayText = showResourceTitle
			? notification.resource_title || subjectLine
			: subjectLine;
	}

	if (useTextWithLineClamp) {
		return (
			<Text className={className} lineClamp={lineClamp}>
				{displayText}
			</Text>
		);
	}

	return <span className={className}>{displayText}</span>;
}
